import { useMemo } from 'react'
import { Control, FieldArrayWithId, useWatch } from 'react-hook-form'
import { AlertTriangle } from 'lucide-react'

import { TextInput } from 'components/FormUtils/TextInput'
import {
  findCampaignColumnVariablesWithMissingValues,
  findUnknownTemplateVariables,
  getMissingValuesLabel,
  mergeVariables,
} from '../email-variables'
import { useCampaignVariables } from '../columns/useCampaignVariables'
import { EmailBodyEditor } from './EmailBodyEditor'

export interface SequenceStepForm {
  id?: number // Bison step ID - present for existing steps, undefined for new
  email_subject: string
  email_body: string
  wait_in_days: number
}

export interface SequenceForm {
  title: string
  steps: SequenceStepForm[]
}

export const defaultStep: SequenceStepForm = {
  email_subject: '',
  email_body: '',
  wait_in_days: 1,
}

interface SequenceStepFormItemProps {
  field: FieldArrayWithId<SequenceForm, 'steps', 'id'>
  index: number
  control: Control<SequenceForm>
  canRemove: boolean
  isLast: boolean
  onRemove: () => void
  campaignId?: number
}

export function SequenceStepFormItem({
  field,
  index,
  control,
  canRemove,
  isLast,
  onRemove,
  campaignId,
}: SequenceStepFormItemProps) {
  const customVariables = useCampaignVariables(campaignId)
  const variables = useMemo(
    () => mergeVariables(customVariables),
    [customVariables]
  )
  const subject = useWatch({
    control,
    name: `steps.${index}.email_subject`,
  })
  const missingSubjectVariables = useMemo(
    () =>
      findCampaignColumnVariablesWithMissingValues(variables, [
        typeof subject === 'string' ? subject : '',
      ]),
    [subject, variables]
  )
  const unknownSubjectVariables = useMemo(
    () =>
      findUnknownTemplateVariables(variables, [
        typeof subject === 'string' ? subject : '',
      ]),
    [subject, variables]
  )

  return (
    <div
      key={field.id}
      className="border border-gray-200 rounded-lg p-4 bg-gray-50"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
            {index + 1}
          </span>
          <span className="text-sm font-medium text-gray-700">
            Step {index + 1}
          </span>
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-sm text-red-600 hover:text-red-800"
          >
            Remove
          </button>
        )}
      </div>

      <div className="space-y-3">
        <TextInput
          name={`steps.${index}.email_subject`}
          control={control}
          label="Subject"
          placeholder="Email subject line"
          rules={{ required: 'Subject is required' }}
        />
        {missingSubjectVariables.length > 0 && (
          <div className="flex items-start gap-1.5 rounded-md border border-warning-border bg-warning-bg-subtle px-2 py-1.5 text-xs text-warning">
            <AlertTriangle className="mt-0.5 size-3 shrink-0" />
            <span>
              Subject uses campaign column variables with missing values:{' '}
              {missingSubjectVariables
                .map(
                  (variable) =>
                    `${variable.label} (${getMissingValuesLabel(variable)})`
                )
                .join(', ')}
            </span>
          </div>
        )}
        {unknownSubjectVariables.length > 0 && (
          <div className="flex items-start gap-1.5 rounded-md border border-warning-border bg-warning-bg-subtle px-2 py-1.5 text-xs text-warning">
            <AlertTriangle className="mt-0.5 size-3 shrink-0" />
            <span>
              Subject uses variables that are not available for this campaign:{' '}
              {unknownSubjectVariables.join(', ')}
            </span>
          </div>
        )}

        <EmailBodyEditor
          name={`steps.${index}.email_body`}
          control={control}
          label="Body"
          placeholder="Write your email content here..."
          campaignId={campaignId}
        />

        {!isLast && (
          <TextInput
            name={`steps.${index}.wait_in_days`}
            control={control}
            label="Wait before next step (days)"
            type="number"
            placeholder="Days to wait before next step"
            rules={{
              required: 'Wait time is required',
              min: { value: 0, message: 'Must be 0 or greater' },
            }}
          />
        )}
      </div>
    </div>
  )
}
